/**
 * Hands an EasyKey clipboard to the Branch EasyKey Chrome extension.
 *
 * The extension's content script on Staff (packages/easykey-extension/src/staff-bridge.js) listens
 * for this page's own postMessage and answers with a `copy-result` carrying the same requestId. If
 * nothing answers in time, the extension isn't installed (or is disabled) in this browser. The
 * channel and message names mirror packages/easykey-extension/src/shared/protocol.js; change both.
 */

const PAGE_CHANNEL = 'branch-easykey';
const COPY = 'copy';
const COPY_RESULT = 'copy-result';

// The bridge answers within a few milliseconds; this only bounds the wait when it isn't there.
export const EASYKEY_RESPONSE_TIMEOUT_MS = 2000;

export class EasyKeyExtensionMissingError extends Error {
  constructor() {
    super('The EasyKey extension did not respond');
    this.name = 'EasyKeyExtensionMissingError';
  }
}

export class EasyKeyCopyError extends Error {
  constructor(reason) {
    super(`The EasyKey extension rejected the copy: ${reason}`);
    this.name = 'EasyKeyCopyError';
    this.reason = reason;
  }
}

/** Resolves with the extension's summary of what it stored; rejects with one of the errors above. */
export function copyToEasyKey(clipboard, { target = window, timeoutMs = EASYKEY_RESPONSE_TIMEOUT_MS } = {}) {
  const requestId = target.crypto.randomUUID();
  const origin = target.location.origin;

  return new Promise((resolve, reject) => {
    const onMessage = (event) => {
      const { data } = event;
      if (
        event.source !== target ||
        event.origin !== origin ||
        data?.channel !== PAGE_CHANNEL ||
        data.type !== COPY_RESULT ||
        data.requestId !== requestId
      ) {
        return;
      }
      cleanup();
      if (data.ok) {
        resolve(data.summary);
      } else {
        reject(new EasyKeyCopyError(data.error));
      }
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new EasyKeyExtensionMissingError());
    }, timeoutMs);
    function cleanup() {
      clearTimeout(timer);
      target.removeEventListener('message', onMessage);
    }

    target.addEventListener('message', onMessage);
    target.postMessage({ channel: PAGE_CHANNEL, type: COPY, requestId, clipboard }, origin);
  });
}
